import { Injectable } from '@angular/core';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldNodeInputDefinitionList,
  FieldsetDeclaration,
  GraphBuilderContext,
} from '@fmnts/declarative-forms';
import {
  ActionNodeName,
  ActionOperatorName,
  ArrayOperatorNodeName,
  FormFieldSource,
  FormNodeName,
  FormOperatorName,
  RxJsNodeName,
  RxJsOperatorNodeName,
} from '@fmnts/declarative-forms/common';
import {
  DeclarativeFormFieldKey,
  DeclarativeFormFieldsetKey,
  declarativeFormFieldPath,
} from '@formunauts/shared/declarative-forms/domain';
import { PhoneAction } from './phone-action.service';

type MaybeInputs = FieldNodeInputDefinitionList | undefined;

const contactDataConfField: FormFieldSource = {
  name: FormNodeName.FieldValue,
  options: {
    path: declarativeFormFieldPath.contactDataOptIn,
  },
};

const fakeContactConsent: FieldNodeInputDefinitionList = {
  // Make field readonly, when radio button is "non-consent"
  isReadonly: {
    ...contactDataConfField,
    pipe: [
      {
        operator: RxJsOperatorNodeName.MapToCondition,
        args: [{ equals: 0, typeSafe: false }],
      },
    ],
  },
  // Unset value, when radio button is "non-consent"
  value: {
    ...contactDataConfField,
    pipe: [
      {
        operator: RxJsOperatorNodeName.FilterByCondition,
        args: [{ equals: 0, typeSafe: false }],
      },
      { operator: RxJsOperatorNodeName.MapTo, args: [null] },
      {
        operator: FormOperatorName.SetUiState,
        args: [{ pristine: true, untouched: true }],
      },
    ],
  },
};

const fakeMobilePhoneValidation: FieldNodeInputDefinitionList = {
  isReadonly: {
    name: ActionNodeName.FromActionEvent,
    options: {
      action: PhoneAction.MobileNumberValidation,
    },
    pipe: [
      {
        operator: ActionOperatorName.MapToIsRunning,
      },
      { operator: RxJsOperatorNodeName.StartWith, args: [false] },
    ],
  },
};

/**
 * Builder for phone field related graph nodes
 */
@Injectable()
export class PhoneFieldGraphNodeBuilder extends AbstractGraphNodeBuilder<
  string,
  string
> {
  createFakeDefinitions(ctx: GraphBuilderContext): MaybeInputs {
    const genericComponent = ctx.injector.get(DeclarativeFieldComponent, null);

    const fieldKey = genericComponent?.field.key;
    let inputs: MaybeInputs;
    if (genericComponent) {
      switch (fieldKey) {
        case DeclarativeFormFieldKey.MobileNumber:
          inputs = this.applyValidationBehavior(inputs, genericComponent);
      }
    }

    if (
      DeclarativeFormFieldsetKey.ContactInfo === genericComponent?.fieldset.key
    ) {
      switch (genericComponent.field.key) {
        case DeclarativeFormFieldKey.PhoneNumber:
        case DeclarativeFormFieldKey.MobileNumber:
          inputs = this.applyContactConsentBehavior(inputs, genericComponent);
      }
    }

    return inputs;
  }

  /**
   * Applies mobile number validation behavior to the given `inputs`
   * if applicable.
   *
   * @param inputs
   * @param genericComponent
   * @returns
   */
  private applyValidationBehavior(
    inputs: MaybeInputs,
    genericComponent: DeclarativeFieldComponent,
  ): MaybeInputs {
    const key = genericComponent.field.key;
    if (!relatedValidationActionInFieldset(key, genericComponent.fieldset)) {
      return inputs;
    }

    return fakeMobilePhoneValidation;
  }

  /**
   * Applies contact (radio button) consent behavior to given `inputs`
   * if applicable.
   *
   * @param inputs
   * @param genericComponent
   * @returns
   */
  private applyContactConsentBehavior(
    inputs: MaybeInputs,
    genericComponent: DeclarativeFieldComponent,
  ): MaybeInputs {
    if (
      contactDataConfField.options?.path &&
      !genericComponent.declaration.get(contactDataConfField.options.path)
    ) {
      return inputs;
    }

    if (!inputs) {
      return fakeContactConsent;
    }

    const isReadonly = !('isReadonly' in inputs)
      ? fakeContactConsent.isReadonly
      : {
          name: RxJsNodeName.CombineLatest,
          options: [inputs.isReadonly, fakeContactConsent.isReadonly],
          pipe: [
            {
              operator: ArrayOperatorNodeName.MapToSomeByCondition,
              args: [{ equals: true }],
            },
            { operator: RxJsOperatorNodeName.StartWith, args: [false] },
          ],
        };

    return {
      ...inputs,
      value: fakeContactConsent.value,
      isReadonly,
    };
  }
}

/**
 * @returns
 * `true` if there is an action in the current fieldset
 * that relates to the current field.
 */
function relatedValidationActionInFieldset(
  key: string,
  fieldset: FieldsetDeclaration,
) {
  if (!key || !fieldset) {
    return false;
  }

  return (
    fieldset.fields.find((f) => {
      const { action, actionConfig } = f as Record<string, any>;
      if (!action || !actionConfig || typeof actionConfig !== 'object') {
        return false;
      }

      return (
        action === PhoneAction.MobileNumberValidation &&
        actionConfig.phone_field_key === key
      );
    }) !== undefined
  );
}
